import { useFormik } from "formik";
import React, { useEffect, useLayoutEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import PageTitle from "../../../components/partials/PageTitle";
import Account from "../../../components/policy/edit/Account";
import Charges from "../../../components/policy/edit/Charges";
import Cover from "../../../components/policy/edit/Cover";
import GeneralInformation from "../../../components/policy/edit/GeneralInformation";
import Interest from "../../../components/policy/edit/Interest";
import {
  getPolicy,
  getPolicyCoverByCategory,
  resetPolicyStatus,
  updatePolicy,
} from "../../../redux/slice/policy/policySlice";
import { policyEdit } from "../../../utils/validators/schema";
import AddCoverModal from "../../../components/modals/policy/AddCoverModal";
import Settings, {
  filterSpecificOffice,
} from "../../../components/policy/edit/Settings";
import { useCallback } from "react";
import { BeatLoader } from "react-spinners";
import Loading from "../../../components/loader/Loading";

const EditPolicy = () => {
  const [hasEditPolicy, setHasEditPolicy] = useState();

  const { single_policy, isSuccess, isLoading, isUpdated } = useSelector(
    (state) => state.policy
  );
  const {
    offices: { offices },
  } = useSelector((state) => state?.office);
  const { gl_accounts } = useSelector((state) => state.accounting);
  const { user, user_permissions } = useSelector((state) => state?.auth?.user);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();

  const [policy, setPolicy] = useState(single_policy?.data);
  const [glAccounts, setGLAccounts] = useState(gl_accounts);
  const [edit, setEdit] = useState(false);

  // breadcrumb nav
  const breadcrumb = {
    from: {
      title: "Policies",
      link: "/admin/all-policies",
    },
    current: {
      title: "edit policy",
      link: null,
    },
  };

  const onSubmit = (values) => {
    let payload = {
      p_general_information: {
        name: values?.name,
        short_name: values?.short_name,
        minimun_duration: values?.minimun_duration,
        currency: values?.currency,
        minimum_age: values?.minimum_age,
        maximum_age: values?.maximum_age,
        description: values?.description,
      },
      p_interest: {
        default_rate: values?.default_interest_rate,
        minimum_rate: values?.minimum_interest_rate,
        maximum_rate: values?.maximum_interest_rate,
      },
      p_account: {
        income_gl: values?.income_gl,
        expense_gl: values?.expense_gl,
        asset_gl: values?.asset_gl,
        libility_gl: values?.libility_gl,
        provision_gl: values?.provision_gl,
        tax_gl: values?.tax_gl,
        over_payment_gl: values?.over_payment_gl,
      },
      expires_at: values?.expires_at,
      charges: [],
      policy_cat_id: 1,
    };
    if (values?.apply_to === "SO") {
      payload = {
        ...payload,
        specific_offices: [
          ...values?.specific_offices?.map((item) => ({
            office_id: item?.value,
          })),
        ],
      };
    } else if (values?.apply_to === "CW") {
      payload = {
        ...payload,
        company_wide: true,
      };
    } else if (values?.apply_to === "RAB") {
      payload = {
        ...payload,
        rab: values.r_a_b,
        apply_sub_offices: values?.apply_to_sub_offices,
      };
    }
    if (values?.cover?.length) {
      payload = {
        ...payload,
        p_cover: [
          ...values.cover.map((item) => ({
            id: item.id,
            cover: item.cover,
          })),
        ],
      };
    }
    dispatch(
      updatePolicy({
        payload,
        id,
      })
    );
  };

  const applyTo = useCallback(() => {
    let result = "";
    if (
      policy?.p_setting[0]?.r_a_b !== "nil" &&
      policy?.p_setting[0]?.r_a_b !== null &&
      policy?.p_setting[0]?.r_a_b !== undefined &&
      policy?.p_setting[0]?.r_a_b !== ""
    ) {
      result = "RAB";
    } else if (
      policy?.p_setting[0]?.company_wide !== "nil" &&
      policy?.p_setting[0]?.company_wide !== null &&
      policy?.p_setting[0]?.company_wide !== undefined &&
      policy?.p_setting[0]?.company_wide !== 0 &&
      policy?.p_setting[0]?.company_wide !== ""
    ) {
      result = "CW";
    } else if (
      policy?.p_setting[0]?.specific_offices !== "nil" &&
      policy?.p_setting[0]?.specific_offices !== null &&
      policy?.p_setting[0]?.specific_offices !== undefined &&
      policy?.p_setting[0]?.specific_offices !== ""
    ) {
      result = "SO";
    }
    return result;
  }, [policy]);

  const {
    errors,
    values,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    setFieldValue,
    getFieldProps,
    resetForm,
    setFieldTouched,
    setFieldError,
  } = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: policy?.p_gen_info?.name || "",
      short_name: policy?.p_gen_info?.short_name || "",
      minimun_duration: policy?.p_gen_info?.minimun_duration || "",
      currency: policy?.p_gen_info?.currency || "",
      minimum_age: policy?.p_gen_info?.minimum_age || "",
      maximum_age: policy?.p_gen_info?.maximum_age || "",
      description: policy?.p_gen_info?.description || "",
      default_interest_rate: policy?.p_interest_info?.default_rate || "",
      minimum_interest_rate: policy?.p_interest_info?.minimum_rate || "",
      maximum_interest_rate: policy?.p_interest_info?.maximum_rate || "",
      income_gl: policy?.p_account_info?.income_gl || "",
      expense_gl: policy?.p_account_info?.expense_gl || "",
      asset_gl: policy?.p_account_info?.asset_gl || "",
      libility_gl: policy?.p_account_info?.libility_gl || "",
      over_payment_gl: policy?.p_account_info?.over_payment_gl || "",
      provision_gl: policy?.p_account_info?.provision_gl || "",
      tax_gl: policy?.p_account_info?.tax_gl || "",
      charges: policy?.p_charges_info || [],
      cover: policy?.p_cover,
      company_wide: policy?.p_setting[0]?.company_wide || null,
      r_a_b: policy?.p_setting[0]?.r_a_b || null,
      apply_to_sub_offices: policy?.p_setting[0]?.apply_to_sub_offices || null,
      specific_offices:
        policy?.p_setting[0]?.specific_offices === "applied"
          ? filterSpecificOffice(offices, policy?.applied_offices)
          : "nil",
      applied_offices: policy?.applied_offices || [],
      expires_at: policy?.p_setting[0]?.expires_at || "",
      apply_to: applyTo(),
    },
    validationSchema: policyEdit,
    validate: (values) => {
      const errors = {};
      if (parseInt(values?.maximum_age) < parseInt(values?.minimum_age)) {
        errors.min = "Minimum age cannot be greater than maximum age";
        errors.max = "Maximum age cannot be less than minimum age";
      }
      if (
        parseFloat(values?.default_interest_rate) <
        parseFloat(values?.minimum_interest_rate)
      ) {
        errors.default_rate = "Default rate cannot be less than minimum rate";
        errors.min_rate = "Minimum rate cannot be greater than default rate";
      }
      if (
        parseFloat(values?.default_interest_rate) >
        parseFloat(values?.maximum_interest_rate)
      ) {
        errors.default_rate =
          "Default rate cannot be greater than maximum rate";
        errors.max_rate = "Maximum rate cannot be less than default rate";
      }
      if (
        parseFloat(values?.maximum_interest_rate) <
        parseFloat(values?.minimum_interest_rate)
      ) {
        errors.min_rate = "Minimum rate cannot be greater than maximum rate";
        errors.max_rate = "Maximum rate cannot be less than minimum rate";
      }
      if (
        (!values.specific_offices || values.specific_offices.length === 0) &&
        values?.apply_to === "SO"
      ) {
        errors.specific_offices = "At least one specific office is required";
      }

      return errors;
    },
    onSubmit,
  });

  useEffect(() => {
    const payload = {
      id: id,
      office_id: user?.office_id,
    };
    dispatch(getPolicy(payload));
    dispatch(getPolicyCoverByCategory(1));
  }, []);

  useEffect(() => {
    setPolicy(single_policy?.data);
    if (gl_accounts) {
      setGLAccounts(gl_accounts);
    }
    if (!isLoading) {
      setEdit(false);
      dispatch(resetPolicyStatus());
    }
  }, [single_policy?.data, gl_accounts, isUpdated]);

  useLayoutEffect(() => {
    if (isSuccess) {
      const payload = {
        id: id,
        office_id: user?.office_id,
      };

      dispatch(getPolicy(payload));
      dispatch(resetPolicyStatus());
    }
  }, [isSuccess, isUpdated]);

  useEffect(() => {
    if (
      !user_permissions?.some(
        (permission) => permission.name === "settings module"
      ) ||
      !user_permissions?.some(
        (permission) => permission.name === "policy configuration module"
      ) ||
      !user_permissions?.some(
        (permission) => permission.name === "view policy details"
      )
    ) {
      navigate("/admin/dashboard");
    }
    setHasEditPolicy(
      user_permissions?.some((permission) => permission.name === "edit policy")
    );
  }, [navigate, user_permissions]);

  // useEffect(() => {
  //   dispatch(getGlAccounts());
  // }, []);

  return (
    <>
      {/* page title */}
      <PageTitle title='Edit Policy' breadcrumb={breadcrumb} />
      {/* end page title */}

      {/* Table row */}

      {!single_policy && (
        <BeatLoader
          height='80'
          width='80'
          radius='9'
          color='#2e3192'
          ariaLabel='three-dots-loading'
          wrapperStyle={{}}
          wrapperClassName=''
          visible={true}
        />
      )}

      {single_policy?.data && (
        <div
          className='position-relative row mt- card p-1'
          style={{ margin: "auto 1px" }}
        >
          <form onSubmit={handleSubmit}>
            <div className='row mb-2'>
              <GeneralInformation
                p_gen_info={policy?.p_gen_info}
                values={values}
                errors={errors}
                edit={edit}
                touched={touched}
                handleBlur={handleBlur}
                handleChange={handleChange}
                setFieldValue={setFieldValue}
              />
            </div>
            <div className='row mb-'>
              <Interest
                rate={policy?.p_interest_info}
                values={values}
                errors={errors}
                edit={edit}
                touched={touched}
                handleBlur={handleBlur}
                handleChange={handleChange}
              />
            </div>
            <div className='row mb-1'>
              <Account
                p_account_info={policy?.p_account_info}
                values={values}
                errors={errors}
                edit={edit}
                touched={touched}
                gl_accounts={glAccounts}
                handleBlur={handleBlur}
                handleChange={handleChange}
                setFieldValue={setFieldValue}
              />
            </div>

            {/* <div className='row mb-3'>
              <Charges
                p_charges_info={policy?.p_charges_info}
                errors={errors}
                edit={edit}
                touched={touched}
                handleBlur={handleBlur}
                setFieldValue={setFieldValue}
                getFieldProps={getFieldProps}
              />
            </div> */}

            <div className='row mb-2'>
              <Cover
                p_cover={policy?.p_cover}
                values={values}
                errors={errors}
                edit={edit}
                touched={touched}
                handleBlur={handleBlur}
                setFieldValue={setFieldValue}
                handleChange={handleChange}
              />
            </div>
            <div className='row mb-2'>
              <Settings
                p_setting={policy?.p_setting}
                values={values}
                errors={errors}
                edit={edit}
                touched={touched}
                handleBlur={handleBlur}
                setFieldValue={setFieldValue}
                handleChange={handleChange}
                getFieldProps={getFieldProps}
                setFieldTouched={setFieldTouched}
                setFieldError={setFieldError}
                policy={policy}
              />
            </div>

            {hasEditPolicy && (
              <div className='my-2 d-flex justify-content-end '>
                {edit ? (
                  <>
                    <button
                      type='button'
                      className='btn btn-sm btn-secondary  me-2'
                      onClick={() => {
                        setEdit(false);
                        resetForm();
                      }}
                    >
                      Close
                    </button>
                    <button type='submit' className='btn btn-sm btn-success'>
                      Save
                    </button>
                  </>
                ) : (
                  <button
                    type='button'
                    className='btn btn-sm btn-info'
                    onClick={() => setEdit(true)}
                  >
                    Edit
                  </button>
                )}
              </div>
            )}
          </form>
          {/* <AddCoverModal modal_id={"add-cover"} /> */}
          {isLoading && edit && <Loading />}
        </div>
      )}
    </>
  );
};

export default EditPolicy;
